import type { ButtonHTMLAttributes, Ref } from "react";
import classNames from "classnames";
import { Button, type ButtonProps } from "@design-system/Button/Button";
import { ChatIcon } from "@icons";
import styles from "./TaskChatButton.module.scss";

interface TaskChatButtonProps extends Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    "children" | "aria-label"
> {
    unreadCount: number;
    /** The button's name without unread messages, where it shows only the icon. */
    label: string;
    /** The button's name with unread messages; without it the icon and count name it. */
    unreadLabel?: string;
    ref?: Ref<HTMLButtonElement>;
}

/**
 * The chat button's `Button` look, the same on a button and a span: square
 * and icon only without unread messages. The square is this stylesheet's,
 * since `Button` can't make an `asChild` span icon only.
 */
const getTaskChatLook = ({
    unreadCount,
    className,
}: {
    unreadCount: number;
    className?: string;
}) =>
    ({
        variant: "primary",
        size: "sm",
        className: classNames(
            styles.chatButton,
            unreadCount > 0 ? styles.unread : styles.iconOnly,
            className,
        ),
    }) satisfies Pick<ButtonProps, "variant" | "size" | "className">;

/** The chat icon, then the unread count in a blue badge when there are unread messages. */
const TaskChatContent = ({ unreadCount }: { unreadCount: number }) => (
    <>
        <ChatIcon aria-hidden />
        {unreadCount > 0 && <span className={styles.count}>{unreadCount}</span>}
    </>
);

/**
 * The button that opens a task's chat, as List and the board show it: a small
 * primary button, icon only without unread messages, else the icon and the
 * unread count in a blue badge.
 */
export const TaskChatButton = ({
    unreadCount,
    label,
    unreadLabel,
    className,
    ...props
}: TaskChatButtonProps) => (
    <Button
        {...props}
        {...getTaskChatLook({ unreadCount, className })}
        aria-label={unreadCount > 0 ? unreadLabel : label}
    >
        <TaskChatContent unreadCount={unreadCount} />
    </Button>
);
